import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Abstract_Trans_List from "./Abstract_Trans_List"
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getMonthName } from '../../Utils/DateUtils';

function PortabilityFpsTable({ date, afsoCode, distCode, month, onDataLoaded }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [totals, setTotals] = useState({});
  const [renderContent, setRenderContent] = useState(null);
  const nextTableRef = useRef(null);
  const currentTableRef = useRef(null);


  const onFpsClick = (fpsId) => {
    if (currentTableRef.current) {
      const { bottom } = currentTableRef.current.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + bottom + 10,
        behavior: "smooth",
      });
    }
    setRenderContent(
      <div ref={nextTableRef}>
        <Abstract_Trans_List date={date} fpsId={fpsId} afsoCode={afsoCode} distCode={distCode} month={month}
          onDataLoaded={() => {
            setTimeout(() => {
              if (nextTableRef.current) {
                const top = nextTableRef.current.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({
                  top: top + 10,
                  behavior: "smooth"
                });
              }
            }, 0);
          }}
        />
      </div>);

  }

  useEffect(() => {
    if (!date || !afsoCode) return;

    setLoading(true);
    setRenderContent(null);
    axios.post(`${process.env.REACT_APP_URL_SIDE}/api/DetailedTrans/fps`, {
      date,
      distCode: distCode,
      afsoCode
    })
      .then(response => {
        setTitle(response.data.header || "FPS Report");
        setData(response.data.data || []);
        if (onDataLoaded) onDataLoaded();
        setMessage(response.data.message || "");
        calculateTotals(response.data.data || []);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching FPS data:", error);
        setData([]);
        setMessage("Internal  error");
        setLoading(false);
      });



  }, [date, afsoCode]);

  const calculateTotals = (data) => {
    if (!data.length) return setTotals({});
    const totalsObj = data.reduce((totals, row) => {
      Object.entries(row).forEach(([key, value]) => {
        if (key !== "District" && typeof value === "number") {
          totals[key] = (totals[key] || 0) + value;
        }
      });
      return totals;
    }, {});
    setTotals(totalsObj);
  };


  const exportToExcel = async () => {
    if (!data.length) return;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('FPS Summary', {
      views: [{ showGridLines: true }]
    });

    const thinBorder = { style: 'thin', color: { argb: 'FF000000' } };
    const headerFill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFEFEFEF' }
    };

    // ===== Title row (row 1) =====
    worksheet.mergeCells('A1:AB1'); // 28 columns
    const titleCell = worksheet.getCell('A1');
    titleCell.value = title || `FPS Transactions ${date || ''}`;
    titleCell.font = { bold: true, size: 14 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 26;

    // ===== Header rows =====
    // Row 2
    worksheet.addRow([
      "Sl.No", "Fps", "Dealer", "Total Cards (A)",
      `Date : ${date}`, "", "", "", "", "", "", "", "", "",
      getMonthName(month), "", "", "", "", "", "", "", "", "",
      "Availed %", "", "",
      "Partial %(Against Availed Cards)(C*100)/E"
    ]);

    // Row 3
    worksheet.addRow([
      "", "", "", "",
      "Trans", "", "", "", "", "", "Availed Cards", "", "", "",
      "Trans", "", "", "", "", "", "Availed Cards", "", "", "",
      "Self %", "Portability %", "Total Availed % (E*100)/A",
      ""
    ]);

    // Row 4
    worksheet.addRow([
      "", "", "", "",
      "Biometric", "Aadhar OTP", "Mobile OTP", "Iris", "Partial Offline", "Total",
      "Portability", "Partial Offline", "Online", "Total",
      "Biometric", "Aadhar OTP", "Mobile OTP", "Iris", "Partial Offline", "Total",
      "Portability (B)", "Partial Offline (C)", "Online (D)", "Total (E=C+D)",
      "", "", "",
      ""
    ]);

    // ===== Merge cells =====
    worksheet.mergeCells('A2:A4'); // Sl.No
    worksheet.mergeCells('B2:B4'); // Fps
    worksheet.mergeCells('C2:C4'); // Dealer
    worksheet.mergeCells('D2:D4'); // Total Cards (A)

    worksheet.mergeCells('E2:N2'); // Date group
    worksheet.mergeCells('O2:X2'); // Current Month group
    worksheet.mergeCells('Y2:AA2'); // Availed % group
    worksheet.mergeCells('AB2:AB4'); // Partial % column

    worksheet.mergeCells('E3:J3'); // Trans (Date) spans 6 cols
    worksheet.mergeCells('K3:N3'); // Availed Cards (Date) spans 4 cols

    worksheet.mergeCells('O3:T3'); // Trans (Month) spans 6 cols
    worksheet.mergeCells('U3:X3'); // Availed Cards (Month) spans 4 cols

    worksheet.mergeCells('Y3:Y4'); // Self %
    worksheet.mergeCells('Z3:Z4'); // Portability %
    worksheet.mergeCells('AA3:AA4'); // Total Availed %

    // ===== Style headers =====
    for (let r = 2; r <= 4; r++) {
      const row = worksheet.getRow(r);
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.fill = headerFill;
        cell.border = {
          top: thinBorder,
          left: thinBorder,
          bottom: thinBorder,
          right: thinBorder
        };
      });
    }

    // ===== Data Rows =====
    data.forEach((row, index) => {
      const r = worksheet.addRow([
        index + 1,
        row.fps_id || "",
        row.del_name || "NA",
        Number(row.total_cards || 0),

        Number(row.avail_bio_trans_today || 0),
        Number(row.avail_otp_trans_today || 0),
        Number(row.avail_motp_trans_today || 0),
        Number(row.avail_iris_trans_today || 0),
        Number(row.avail_poff_trans_today || 0),
        Number(row.avail_trans_today || 0),

        Number(row.avail_port_cards_today || 0),
        Number(row.avail_poff_cards_today || 0),
        Number(row.online_cards_today || 0),
        Number(row.avail_cards_today || 0),

        Number(row.avail_bio_trans || 0),
        Number(row.avail_otp_trans || 0),
        Number(row.avail_motp_trans || 0),
        Number(row.avail_iris_trans || 0),
        Number(row.avail_poff_trans || 0),
        Number(row.avail_trans || 0),

        Number(row.avail_port_cards || 0),
        Number(row.avail_poff_cards || 0),
        Number(row.online_cards || 0),
        Number(row.avail_cards || 0),

        Number(row.self_trans_per || 0).toFixed(2),
        Number(row.port_trans_per || 0).toFixed(2),
        Number(row.total_availed || 0).toFixed(2),
        Number(row.partial || 0).toFixed(2)
      ]);

      r.eachCell((cell, colNumber) => {
        if (colNumber === 2 || colNumber === 3) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        if (colNumber >= 4) cell.numFmt = '0.00';
        cell.border = {
          top: thinBorder,
          left: thinBorder,
          bottom: thinBorder,
          right: thinBorder
        };
      });
    });

    // ===== Totals Row =====
    const totalRowIndex = worksheet.lastRow.number + 1;
    const totalRow = worksheet.addRow([
      "Total", "", "",
      Number(totals.total_cards || 0),
      Number(totals.avail_bio_trans_today || 0),
      Number(totals.avail_otp_trans_today || 0),
      Number(totals.avail_motp_trans_today || 0),
      Number(totals.avail_iris_trans_today || 0),
      Number(totals.avail_poff_trans_today || 0),
      Number(totals.avail_trans_today || 0),
      Number(totals.avail_port_cards_today || 0),
      Number(totals.avail_poff_cards_today || 0),
      Number(totals.online_cards_today || 0),
      Number(totals.avail_cards_today || 0),
      Number(totals.avail_bio_trans || 0),
      Number(totals.avail_otp_trans || 0),
      Number(totals.avail_motp_trans || 0),
      Number(totals.avail_iris_trans || 0),
      Number(totals.avail_poff_trans || 0),
      Number(totals.avail_trans || 0),
      Number(totals.avail_port_cards || 0),
      Number(totals.avail_poff_cards || 0),
      Number(totals.online_cards || 0),
      Number(totals.avail_cards || 0),
      "", "",
      Number(totals.total_cards ? ((totals.avail_cards * 100) / totals.total_cards).toFixed(2) : 0),
      Number(totals.avail_cards ? ((totals.avail_poff_cards * 100) / totals.avail_cards).toFixed(2) : 0)
    ]);

    worksheet.mergeCells(`A${totalRowIndex}:C${totalRowIndex}`);
    totalRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      if (cell.col >= 4) cell.numFmt = '0.00';
      cell.border = {
        top: thinBorder,
        left: thinBorder,
        bottom: thinBorder,
        right: thinBorder
      };
    });

    // ===== Column widths =====
    worksheet.columns = [
      { width: 8 },  // A Sl.No
      { width: 12 }, // B Fps
      { width: 22 }, // C Dealer
      { width: 16 }, // D Total Cards

      { width: 14 }, // E Biometric (Date)
      { width: 14 }, // F Aadhar OTP (Date)
      { width: 14 }, // G Mobile OTP (Date)
      { width: 14 }, // H Iris (Date)
      { width: 16 }, // I Partial Offline (Date)
      { width: 12 }, // J Total (Date)

      { width: 14 }, // K Portability (Date)
      { width: 16 }, // L Partial Offline (Date)
      { width: 12 }, // M Online (Date)
      { width: 12 }, // N Total (Date)

      { width: 14 }, // O Biometric (Month)
      { width: 14 }, // P Aadhar OTP (Month)
      { width: 14 }, // Q Mobile OTP (Month)
      { width: 14 }, // R Iris (Month)
      { width: 16 }, // S Partial Offline (Month)
      { width: 12 }, // T Total (Month)

      { width: 14 }, // U Portability (Month)
      { width: 16 }, // V Partial Offline (Month)
      { width: 12 }, // W Online (Month)
      { width: 14 }, // X Total (Month)

      { width: 12 }, // Y Self %
      { width: 14 }, // Z Portability %
      { width: 16 }, // AA Total Availed %
      { width: 20 }  // AB Partial %
    ];

    // ===== Save file =====
    const safeDate = (date || '').toString().replace(/[\/\\: ]+/g, '_') || new Date().toISOString().slice(0, 10);
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${title}_${safeDate}.xlsx`);
  };



  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
      <CircularProgress />
    </Box></div>
  }

  return (
    <div>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Box sx={{ display: 'flex' }}>
          {CircularProgress}
        </Box></div>
      ) : data.length > 0 ? (
        <div>

          <table align="center" border={1} ref={currentTableRef}>
            <thead>
              <tr className="tableheader" >
                <th colSpan={28}>{title}</th>
              </tr>
              <tr className="tableheader" >
                <th rowSpan={3}>Sl.No</th>
                <th rowSpan={3}>Fps</th>
                <th rowSpan={3}>Dealer</th>
                <th rowSpan={3}>Total Cards (A)</th>
                <th colSpan={10}>Date : {date}</th>
                <th colSpan={10}>{getMonthName(month)}</th>
                <th colSpan={3}> Availed % </th>
                <th rowSpan={3}>Partial %(Against Availed Cards)(C*100)/E</th>
              </tr>
              <tr className="tableheader" >

                <th colSpan={6}>Trans</th>
                <th colSpan={4}>Availed Cards</th>
                <th colSpan={6}>Trans</th>
                <th colSpan={4}>Availed Cards</th>
                <th rowSpan={2}>Self %</th>
                <th rowSpan={2}>Portability %</th>
                <th rowSpan={2}>Total Availed % (E*100)/A</th>

              </tr>
              <tr className="tableheader">
                <th>Biometric</th>
                <th>Aadhar OTP</th>
                <th>Mobile OTP</th>
                <th>Iris</th>
                <th>Partial Offline</th>
                <th>Total</th>
                <th>Portability</th>
                <th>Partial Offline</th>
                <th>Online</th>
                <th>Total</th>
                <th>Biometric</th>
                <th>Aadhar OTP</th>
                <th>Mobile OTP</th>
                <th>Iris</th>
                <th>Partial Offline</th>
                <th>Total</th>
                <th>Portability (B)</th>
                <th>Partial Offline (C)</th>
                <th>Online (D)</th>
                <th>Total (E = C+D)</th>
              </tr>


            </thead>
            <tbody>
              {data.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td onClick={() => onFpsClick(row.fps_id)} style={{ cursor: 'pointer', color: 'blue' }}    ><u>{row.fps_id}</u></td>
                  <td style={{ textAlign: 'left' }} >{row.del_name || "NA"}</td>
                  <td style={{ textAlign: 'right' }} >{row.total_cards || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_bio_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_otp_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_motp_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_iris_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_poff_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_trans_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_port_cards_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_poff_cards_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.online_cards_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_cards_today || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_bio_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_otp_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_motp_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_iris_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_poff_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_trans || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_port_cards || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_poff_cards || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.online_cards || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{row.avail_cards || "0"}</td>
                  <td style={{ textAlign: 'right' }} >{Number(row.self_trans_per || "0").toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }} >{Number(row.port_trans_per || "0").toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }} >{Number(row.total_availed || "0").toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }} >{Number(row.partial || "0").toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="tableheader" >
                <th colSpan={3}>Total</th>
                <th style={{ textAlign: 'right' }} >{totals.total_cards || "0"}</th>
                <td style={{ textAlign: 'right' }} >{totals.avail_bio_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_otp_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_motp_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_iris_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_poff_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_trans_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_port_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_poff_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.online_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_cards_today || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_bio_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_otp_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_motp_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_iris_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_poff_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_trans || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_port_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_poff_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.online_cards || "0"}</td>
                <td style={{ textAlign: 'right' }} >{totals.avail_cards || "0"}</td>
                <td></td>
                <td></td>
                <td style={{ textAlign: 'right' }} >{Number(totals.avail_cards * 100 / totals.total_cards || "0").toFixed(2)}</td>
                <td style={{ textAlign: 'right' }} >{Number(totals.avail_poff_cards * 100 / totals.avail_cards || "0").toFixed(2)}</td>
              </tr>
              <tr align="center"  >
                <td colSpan={28}>
                  <button className="button button2" onClick={exportToExcel} >
                    Export to Excel
                  </button>
                </td>
              </tr>
            </tfoot>

          </table>
        </div>
      ) : (

        <div class="response" align="center" >
          {message}
        </div>

      )}
      <br />
      <br />   <br></br>

      {renderContent}
    </div>
  );
}

export default PortabilityFpsTable;
